import { create } from '@thoughtspot/logger/esm/logger';
import { TSToChartEvent, } from '@thoughtspot/ts-chart-sdk';
import _ from 'lodash';
import { ErrorTypes } from '../../../components/chart-error/chart-error.helper';
import { CustomChartError } from '../../../components/chart-error/chart-error.util';
import { sendMessage } from '../../../services/post-message-event-dispatcher';
import { processLegacyChartModelToCustomChartModel } from '../../../utils/chart-model-adaptor.util';
import { getIsChartSettingsV2EnabledValue } from '../../../utils/chart-settings/custom-chart-schema-hydrator.util';
import { buildCustomVisualProps } from '../../../utils/chart-settings/custom-visual-props';
import { sanitizeCustomVisualPropsV2 } from '../../../utils/chart-settings/helpers';
import { migrateChartSettingsV1ToV2, shouldMigrateSettings, } from '../../../utils/chart-settings-migration';
import { isCustomChartType, omitTypenameProperty, } from '../../../utils/common-util';
import { mapSDKConfigToCustomChartConfig } from '../../../utils/custom-chart-config-adaptor.util';
import { isDataLimitExceedError } from '../../../utils/data-query.util';
import { triggerUpdateChartModel } from '../update-chart-model';
import { triggerUpdateVisualProps } from '../update-visual-props';
import { validateCustomVisualProps } from '../validate-visual-props';
import { validateInitializeEventResponsePayload } from './util';
const logger = create('ts-to-chart > event-callbacks > initialize-custom-chart');
export const initializeCustomChart = async (commonProps, eventProps, emitEvent) => {
    const { componentId, iFrameWindow: iframeRef, appUrl } = commonProps;
    const { chartModel, appConfig, renderComplete, hostUrl, getAnswerDataByQuery, updateChartContext, setChartConfigQueries, containerElSelector, configOptions, updateCustomVisualProps, otelContext, otelEnabled, } = eventProps;
    const sdkChartModel = processLegacyChartModelToCustomChartModel(chartModel, []);
    const isLiveboardContext = !!appConfig?.appOptions?.isLiveboardContext;
    const isChartSettingsV2Enabled = getIsChartSettingsV2EnabledValue(appConfig);
    let updatedChartModel = chartModel;
    if (shouldMigrateSettings(chartModel.customVisualProps, chartModel.config.chartType, isChartSettingsV2Enabled)) {
        const migratedVisualProps = await migrateChartSettingsV1ToV2(chartModel.customVisualProps, chartModel.config.chartType);
        updatedChartModel = {
            ...chartModel,
            customVisualProps: migratedVisualProps,
        };
        if (!isLiveboardContext &&
            !_.isEqual(migratedVisualProps, chartModel.customVisualProps)) {
            updateCustomVisualProps(migratedVisualProps, true);
        }
        sdkChartModel.visualProps = migratedVisualProps;
    }
    const payload = {
        componentId,
        chartModel: sdkChartModel,
        appConfig,
        hostUrl,
        containerElSelector,
    };
    return sendMessage(iframeRef, {
        eventType: TSToChartEvent.Initialize,
        payload,
    }, appUrl)
        .then(async (res) => {
        const validationResponse = validateInitializeEventResponsePayload(res.data, updatedChartModel.columns.map(col => col.column));
        if (!validationResponse.isValid) {
            logger.error('Invalid response received from chart', validationResponse.validationErrorMessage);
            throw new CustomChartError(ErrorTypes.INIT_INVALID_CONFIG_ERROR);
        }
        const { isConfigValid, defaultChartConfig: defaultSDKChartConfig, chartConfigEditorDefinition, visualPropEditorDefinition, allowedConfigurations, persistedVisualPropKeys, chartConfigParameters, } = res.data;
        logger.debug('Init Response from SDK:', res, defaultSDKChartConfig);
        let sdkChartConfig = sdkChartModel.config.chartConfig;
        if (!isConfigValid) {
            if (!_.isEmpty(sdkChartConfig)) {
                logger.error('Cannot render the chart. Chart config is not valid.', sdkChartConfig);
                throw new CustomChartError(ErrorTypes.INIT_INVALID_CONFIG_ERROR);
            }
            if (_.isEmpty(defaultSDKChartConfig)) {
                logger.error('Cannot render the chart as default chart configuration is not possible.', defaultSDKChartConfig);
                throw new CustomChartError(ErrorTypes.INIT_CONFIG_IMPOSSIBLE_ERROR);
            }
            sdkChartConfig = defaultSDKChartConfig;
        }
        updatedChartModel = {
            ...updatedChartModel,
            config: {
                ...updatedChartModel.config,
                customChartConfig: mapSDKConfigToCustomChartConfig(sdkChartConfig),
            },
        };
        let deferredError = null;
        try {
            await triggerUpdateChartModel(commonProps, {
                chartModel: updatedChartModel,
                renderComplete,
                getAnswerDataByQuery,
                options: { configOptions },
                setChartConfigQueries,
                otelContext,
                otelEnabled,
            }, emitEvent);
        }
        catch (error) {
            logger.error('Error in triggerUpdateChartModel', error);
            if (isDataLimitExceedError(error)) {
                deferredError = error;
            }
            else {
                throw error;
            }
        }
        let currentVisualPropDefinition = visualPropEditorDefinition;
        let currentChartConfigDefinition = chartConfigEditorDefinition;
        const updateCurrentDefinitions = ({ updatedVisualPropEditorDefinition, updatedChartConfigEditorDefinition, }) => {
            currentVisualPropDefinition = updatedVisualPropEditorDefinition;
            currentChartConfigDefinition = updatedChartConfigEditorDefinition;
        };
        const { customVisualProps: validatedCustomVisualProps, } = await validateCustomVisualProps(commonProps, {
            visualProps: updatedChartModel.customVisualProps,
            updateCustomChartContext: updateCurrentDefinitions,
        }, emitEvent);
        let overrideColumnProperties = [];
        if (updatedChartModel.visualOverrides?.overrides?.columnProperties) {
            overrideColumnProperties = updatedChartModel.visualOverrides
                ?.overrides?.columnProperties;
        }
        const legacyColumnProperties = (updatedChartModel.vizProp
            ?.columnProperties ?? []);
        const customVisualProps = buildCustomVisualProps({
            isChartSettingsV2Enabled,
            visualPropEditorDefinition: currentVisualPropDefinition,
            existingCustomVisualProps: validatedCustomVisualProps ||
                updatedChartModel.customVisualProps,
            persistedVisualPropKeys,
            overrideColumnProperties,
            legacyColumnProperties,
            isCustomChart: isCustomChartType(updatedChartModel.config.chartType),
            chartColumns: sdkChartModel.columns,
        });
        await triggerUpdateVisualProps(commonProps, {
            visualProps: customVisualProps,
        }, emitEvent, false);
        updateChartContext(currentChartConfigDefinition, currentVisualPropDefinition, isConfigValid ? null : sdkChartConfig, allowedConfigurations, chartConfigParameters, persistedVisualPropKeys);
        const sanitizedExistingSettings = isChartSettingsV2Enabled &&
            !_.isString(updatedChartModel.customVisualProps)
            ? sanitizeCustomVisualPropsV2(updatedChartModel.customVisualProps, currentVisualPropDefinition, persistedVisualPropKeys)
            : updatedChartModel.customVisualProps;
        const existingSettingsOnly = _.isString(sanitizedExistingSettings)
            ? sanitizedExistingSettings
            : omitTypenameProperty(sanitizedExistingSettings);
        if (!isLiveboardContext &&
            !_.isEqual(existingSettingsOnly, customVisualProps)) {
            updateCustomVisualProps(customVisualProps, true);
        }
        if (deferredError) {
            throw deferredError;
        }
        return sendMessage(iframeRef, { eventType: TSToChartEvent.InitializeComplete }, appUrl);
    })
        .catch((err) => {
        logger.error('App init failed!', err);
        throw err;
    })
        .finally(() => {
        logger.debug('App init completed');
    });
};
export const triggerInitializeComplete = (commonProps) => {
    const { iFrameWindow: iframeRef, appUrl } = commonProps;
    return sendMessage(iframeRef, { eventType: TSToChartEvent.InitializeComplete }, appUrl);
};
//# sourceMappingURL=index.js.map